import axios from 'axios';
import toast from 'react-hot-toast';
import {
  emitSessionExpired,
  getNavigate,
  getRefreshInProgress,
  setRefreshInProgress,
} from './authBridge';
import { pushNetwork } from '../utils/diagnostics/diagnosticsBuffer';

const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

const getCsrfToken = () => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    const csrfToken = getCsrfToken();
    if (csrfToken && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method?.toUpperCase())) {
      config.headers[CSRF_HEADER_NAME] = csrfToken;
    }

    const requestId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    config.headers['X-Request-Id'] = requestId;
    config.metadata = { startTime: performance.now(), requestId };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function recordNetwork(config, status) {
  if (!config) return;
  const start = config.metadata?.startTime;
  const durationMs = typeof start === 'number' ? performance.now() - start : null;
  pushNetwork({
    method: config.method,
    url: config.url,
    status,
    durationMs,
    requestId: config.metadata?.requestId
  });
}

// Map refresh endpoint errors to user-facing messages
function mapRefreshError(error) {
  const serverMsg = error?.response?.data?.error;
  if (!error.response) {
    return { code: 'NETWORK', message: 'Unable to reach the server. Please check your connection and log in again.' };
  }
  switch (serverMsg) {
    case 'No refresh token.':
    case 'Refresh token expired.':
      return { code: 'SESSION_EXPIRED', message: 'Your session has expired. Please log in again.' };
    case 'Refresh token revoked.':
      return { code: 'SESSION_REVOKED', message: 'Your session was ended. Please log in again.' };
    case 'Account inactive or not found.':
      return { code: 'ACCOUNT_INACTIVE', message: 'Your account is no longer active. Please contact your administrator.' };
    default:
      return { code: 'AUTH_ERROR', message: 'An authentication error occurred. Please log in again.' };
  }
}

// Response interceptor: auto-refresh access token on 401, then retry the failed request
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    recordNetwork(response.config, response.status);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    recordNetwork(originalRequest, error.response?.status ?? 0);

    // Only handle 401s, skip login/password reset pages and refresh endpoint itself
    const pathname = window.location.pathname;
    const isPublicAuthPage = pathname.includes('/login') ||
                             pathname.includes('/forgot-password') ||
                             pathname.includes('/reset-password');
    if (
      error.response?.status !== 401 ||
      isPublicAuthPage ||
      originalRequest.url === '/auth/refresh' ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    // If already refreshing (interceptor or proactive), queue this request
    if (isRefreshing || getRefreshInProgress()) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => api(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;
    setRefreshInProgress(true);

    try {
      await api.post('/auth/refresh');
      processQueue(null);
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);

      // Initial auth-check calls opt out of the session-expired UX: a failed refresh
      // there just means "not logged in yet", so reject quietly and let the caller
      // (and ProtectedRoute's redirect to /login) handle it — no alarming toast.
      if (originalRequest.suppressAuthRedirect) {
        return Promise.reject(refreshError);
      }

      const reason = mapRefreshError(refreshError);
      emitSessionExpired(reason);
      toast.error(reason.message, { duration: 6000 });

      const navigate = getNavigate();
      if (navigate) {
        navigate('/login', { replace: true });
      } else {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
      setRefreshInProgress(false);
    }
  }
);

export default api;

// API service functions
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get(`/auth/reset-password/${token}`),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

export const travelRequestAPI = {
  getAll: (params) => api.get('/travel-requests', { params }),
  getAllAdmin: (params) => api.get('/travel-requests/all', { params }),
  getTeam: (params) => api.get('/travel-requests/team', { params }),
  getStats: (params) => api.get('/travel-requests/stats', { params }),
  getTeamStats: (params) => api.get('/travel-requests/team/stats', { params }),
  getById: (id) => api.get(`/travel-requests/${id}`),
  create: (data) => api.post('/travel-requests', data),
  update: (id, data) => api.put(`/travel-requests/${id}`, data),
  delete: (id) => api.delete(`/travel-requests/${id}`),
  copyItinerary: (id, target_traveler_ID) =>
    api.post(`/travel-requests/${id}/copy`, { target_traveler_ID }),
  updateAspect: (id, aspect, requested) =>
    api.put(`/travel-requests/${id}/aspects/${aspect}`, { requested }),
  updateNotes: (id, data) => api.put(`/travel-requests/${id}/notes`, data),
  addPromotion: (id, promotion_ID) =>
    api.post(`/travel-requests/${id}/promotions`, { promotion_ID }),
  addLeg: (id, data) => api.post(`/travel-requests/${id}/legs`, data),
  deleteLeg: (legId) => api.delete(`/travel-requests/legs/${legId}`),
  getTravelersByPromotion: (promotionIds, excludeTravelerId) =>
    api.get('/travel-requests/travelers-by-promotion', {
      params: { promotion_ids: promotionIds.join(','), exclude_traveler_id: excludeTravelerId }
    }),
  getTravelersForPromotions: (promotionIds, excludeTravelerId) =>
    api.get('/travel-requests/travelers-for-promotions', {
      params: { promotion_ids: promotionIds.join(','), exclude_traveler_id: excludeTravelerId }
    }),
  getGroundPassengers: (requestId, groundId) =>
    api.get(`/travel-requests/${requestId}/ground/${groundId}/passengers`),
  saveGroundPassengers: (requestId, groundId, passengerIds) =>
    api.post(`/travel-requests/${requestId}/ground/${groundId}/passengers`, { passenger_ids: passengerIds }),
  createTeamLodging: (data) => api.post('/travel-requests/team-lodging', data),
  getLinkablePassengers: (requestId) => api.get(`/travel-requests/${requestId}/linkable-passengers`),
  getAvailableRentals: (requestId) => api.get(`/travel-requests/${requestId}/available-rentals`),
  getCompanyVehicleSummary: (params) => api.get('/travel-requests/company-vehicles', { params }),
  getFlightSummary: (params) => api.get('/travel-requests/flights', { params }),
  getLodgingSummary: (params) => api.get('/travel-requests/lodging', { params }),
  getCarRentalSummary: (params) => api.get('/travel-requests/car-rentals', { params }),
  getDetailedExport: (params) => api.get('/travel-requests/detailed-export', { params }),
};

export const approvalAPI = {
  getPending: (params = {}) => api.get('/approvals/pending', { params }),
  getMyTravelers: (params = {}) => api.get('/approvals/my-travelers', { params }),
  approve: (id, notes) => api.post(`/approvals/${id}/approve`, { notes }),
  setInProcess: (id, notes) => api.post(`/approvals/${id}/set-in-process`, { notes }),
  complete: (id, notes) => api.post(`/approvals/${id}/complete`, { notes }),
  reject: (id, reason) => api.post(`/approvals/${id}/reject`, { reason }),
  requestChanges: (id, changes) => api.post(`/approvals/${id}/request-changes`, { changes_requested: changes }),
  updateStatus: (id, status_id, reason) =>
    api.put(`/approvals/${id}/status`, {
      status_id,
      ...(reason ? { reason } : {}),
    }),
  notifyApprover: (id) => api.post(`/approvals/${id}/notify-approver`),
  getStats: () => api.get('/approvals/stats'),
};

export const bookingAPI = {
  getReady: () => api.get('/bookings/ready'),
  addFlight: (requestId, data) => api.post(`/bookings/${requestId}/flights`, data),
  updateFlight: (flightId, data) => api.put(`/bookings/flights/${flightId}`, data),
  deleteFlight: (flightId) => api.delete(`/bookings/flights/${flightId}`),
  addGround: (requestId, data) => api.post(`/bookings/${requestId}/ground`, data),
  updateGround: (groundId, data) => api.put(`/bookings/ground/${groundId}`, data),
  deleteGround: (groundId) => api.delete(`/bookings/ground/${groundId}`),
  addCarRental: (groundId, data) => api.post(`/bookings/ground/${groundId}/rental`, data),
  updateCarRental: (rentalId, data) => api.put(`/bookings/car-rental/${rentalId}`, data),
  deleteCarRental: (rentalId) => api.delete(`/bookings/car-rental/${rentalId}`),
  addLodging: (requestId, data) => api.post(`/bookings/${requestId}/lodging`, data),
  getStats: () => api.get('/bookings/stats'),
  // Booking status lifecycle (1 = active, 2 = changed, 3 = canceled)
  setFlightStatus: (flightId, body) => api.patch(`/bookings/flights/${flightId}/status`, body),
  setCarRentalStatus: (rentalId, body) => api.patch(`/bookings/car-rental/${rentalId}/status`, body),
  setLodgingStatus: (roomId, body) => api.patch(`/bookings/lodging/${roomId}/status`, body),
  // Car rental passenger management
  getCarRentalPassengers: (rentalId) => api.get(`/bookings/car-rental/${rentalId}/passengers`),
  linkCarRentalPassengers: (rentalId, groundSegmentIds) =>
    api.post(`/bookings/car-rental/${rentalId}/passengers`, { ground_segment_ids: groundSegmentIds }),
  unlinkCarRentalPassenger: (rentalId, groundSegmentId) =>
    api.delete(`/bookings/car-rental/${rentalId}/passengers/${groundSegmentId}`),
};

export const lookupAPI = {
  getTravelReasons: () => api.get('/lookups/travel-reasons'),
  createTravelReason: (data) => api.post('/lookups/travel-reasons', data),
  updateTravelReason: (id, data) => api.put(`/lookups/travel-reasons/${id}`, data),
  deleteTravelReason: (id) => api.delete(`/lookups/travel-reasons/${id}`),
  getTravelStatuses: () => api.get('/lookups/travel-statuses'),
  getDayParts: () => api.get('/lookups/day-parts'),
  getEmployeeRoles: () => api.get('/lookups/employee-roles'),
  getAirlines: (search) => api.get('/lookups/airlines', { params: { search } }),
  getAirports: (search) => api.get('/lookups/airports', { params: { search } }),
  getAllAirports: (search) => api.get('/lookups/all-airports', { params: { search } }),
  getGroundTypes: () => api.get('/lookups/ground-types'),
  createGroundType: (data) => api.post('/lookups/ground-types', data),
  updateGroundType: (id, data) => api.put(`/lookups/ground-types/${id}`, data),
  deleteGroundType: (id) => api.delete(`/lookups/ground-types/${id}`),
  getCompanyVehicles: (search) => api.get('/lookups/company-vehicles', { params: { search } }),
  createCompanyVehicle: (data) => api.post('/lookups/company-vehicles', data),
  updateCompanyVehicle: (id, data) => api.put(`/lookups/company-vehicles/${id}`, data),
  deleteCompanyVehicle: (id) => api.delete(`/lookups/company-vehicles/${id}`),
  getCarRentalAgencies: (search, limit) => api.get('/lookups/car-rental-agencies', { params: { search, limit } }),
  getSettingsCounts: () => api.get('/lookups/settings-counts'),
  createCarRentalAgency: (data) => api.post('/lookups/car-rental-agencies', data),
  updateCarRentalAgency: (id, data) => api.put(`/lookups/car-rental-agencies/${id}`, data),
  deleteCarRentalAgency: (id) => api.delete(`/lookups/car-rental-agencies/${id}`),
  getHotelChains: (search) => api.get('/lookups/hotel-chains', { params: { search } }),
  getCurrencies: () => api.get('/lookups/currencies'),
  getDepartments: () => api.get('/lookups/departments'),
  getTracks: () => api.get('/lookups/tracks'),
  getPromotions: (params) => api.get('/lookups/promotions', { params }),
  getPromotionsWithTravel: (search) => api.get('/lookups/promotions-with-travel', { params: { search } }),
  getSeries: () => api.get('/lookups/series'),
  getBrands: () => api.get('/lookups/brands'),
  getOffices: () => api.get('/lookups/offices'),
  getEmployeeTypes: () => api.get('/lookups/employee-types'),
  getAppRoles: () => api.get('/lookups/app-roles'),
};

export const employeeAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  getTravelLeads: () => api.get('/employees/travel-leads/list'),
  updateRole: (id, roleId) => api.put(`/employees/${id}/role`, { app_role_ID: roleId }),
  updateDefaultRole: (id, roleId) => api.put(`/employees/${id}/default-role`, { default_employee_role_ID: roleId }),
  updateDepartment: (id, departmentId) => api.put(`/employees/${id}/department`, { department_ID: departmentId }),
  updateProfile: (id, data) => api.put(`/employees/${id}/profile`, data),
  getActivity: (id) => api.get(`/employees/${id}/activity`),
};

export const travelerAPI = {
  getAll: (params) => api.get('/travelers', { params }),
  getTeamMembers: (params) => api.get('/travelers/team/members', { params }),
  getMyAssignees: (params) => api.get('/travelers/my-assignees', { params }),
  getById: (id) => api.get(`/travelers/${id}`),
  create: (data) => api.post('/travelers', data),
  update: (id, data) => api.put(`/travelers/${id}`, data),
  getHistory: (id) => api.get(`/travelers/${id}/history`),
  checkProfileComplete: (id) => api.get(`/travelers/${id}/profile-complete`),
  // Rewards program lookups
  getAirlinePrograms: () => api.get('/travelers/lookups/airline-programs'),
  getHotelPrograms: () => api.get('/travelers/lookups/hotel-programs'),
  getCarRentalPrograms: () => api.get('/travelers/lookups/car-rental-programs'),
  // Rewards program management (admin)
  createAirlineProgram: (data) => api.post('/travelers/lookups/airline-programs', data),
  updateAirlineProgram: (id, data) => api.put(`/travelers/lookups/airline-programs/${id}`, data),
  deleteAirlineProgram: (id) => api.delete(`/travelers/lookups/airline-programs/${id}`),
  createHotelProgram: (data) => api.post('/travelers/lookups/hotel-programs', data),
  updateHotelProgram: (id, data) => api.put(`/travelers/lookups/hotel-programs/${id}`, data),
  deleteHotelProgram: (id) => api.delete(`/travelers/lookups/hotel-programs/${id}`),
  createCarRentalProgram: (data) => api.post('/travelers/lookups/car-rental-programs', data),
  updateCarRentalProgram: (id, data) => api.put(`/travelers/lookups/car-rental-programs/${id}`, data),
  deleteCarRentalProgram: (id) => api.delete(`/travelers/lookups/car-rental-programs/${id}`),
  // Airline rewards
  addAirlineReward: (travelerId, data) => api.post(`/travelers/${travelerId}/airline-rewards`, data),
  updateAirlineReward: (rewardId, data) => api.put(`/travelers/airline-rewards/${rewardId}`, data),
  deleteAirlineReward: (rewardId) => api.delete(`/travelers/airline-rewards/${rewardId}`),
  // Hotel rewards
  addHotelReward: (travelerId, data) => api.post(`/travelers/${travelerId}/hotel-rewards`, data),
  updateHotelReward: (rewardId, data) => api.put(`/travelers/hotel-rewards/${rewardId}`, data),
  deleteHotelReward: (rewardId) => api.delete(`/travelers/hotel-rewards/${rewardId}`),
  // Car rental rewards
  addCarRentalReward: (travelerId, data) => api.post(`/travelers/${travelerId}/car-rental-rewards`, data),
  updateCarRentalReward: (rewardId, data) => api.put(`/travelers/car-rental-rewards/${rewardId}`, data),
  deleteCarRentalReward: (rewardId) => api.delete(`/travelers/car-rental-rewards/${rewardId}`),
};

export const roomBlockAPI = {
  getAll: (params) => api.get('/room-blocks', { params }),
  getById: (id) => api.get(`/room-blocks/${id}`),
  create: (data) => api.post('/room-blocks', data),
  update: (id, data) => api.put(`/room-blocks/${id}`, data),
  delete: (id, force = false) => api.delete(`/room-blocks/${id}`, { params: { force } }),
  // Rooms
  addRoom: (blockId, data) => api.post(`/room-blocks/${blockId}/rooms`, data),
  addRoomsBulk: (blockId, data) => api.post(`/room-blocks/${blockId}/rooms/bulk`, data),
  updateRoom: (roomId, data) => api.put(`/room-blocks/rooms/${roomId}`, data),
  deleteRoom: (roomId) => api.delete(`/room-blocks/rooms/${roomId}`),
  // Assignments
  assignRoom: (roomId, data) => api.post(`/room-blocks/rooms/${roomId}/assign`, data),
  updateAssignment: (assignmentId, data) => api.put(`/room-blocks/assignments/${assignmentId}`, data),
  removeAssignment: (assignmentId) => api.delete(`/room-blocks/assignments/${assignmentId}`),
  // Lookups
  getAvailableRooms: (promotionId, params) => api.get(`/room-blocks/available/${promotionId}`, { params }),
  getRoomTypes: () => api.get('/room-blocks/lookups/room-types'),
  getHotels: (search) => api.get('/room-blocks/lookups/hotels', { params: { search } }),
  createHotel: (data) => api.post('/room-blocks/hotels', data),
  getEligibleTravelers: (blockId) => api.get(`/room-blocks/${blockId}/eligible-travelers`),
};

export const departmentAPI = {
  getAll: (params) => api.get('/departments', { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (data) => api.post('/departments', data),
  update: (id, data) => api.put(`/departments/${id}`, data),
  delete: (id) => api.delete(`/departments/${id}`),
  // Lookups
  getEmployees: (search) => api.get('/departments/lookups/employees', { params: { search } }),
  getSeries: () => api.get('/departments/lookups/series'),
};

export const locationAPI = {
  // Hotels
  getHotels: (params) => api.get('/locations/hotels', { params }),
  getHotel: (id) => api.get(`/locations/hotels/${id}`),
  getHotelWithBlocks: (id) => api.get(`/locations/hotels/${id}/details`),
  createHotel: (data) => api.post('/locations/hotels', data),
  updateHotel: (id, data) => api.put(`/locations/hotels/${id}`, data),
  deleteHotel: (id) => api.delete(`/locations/hotels/${id}`),
  // Travel Airports
  getTravelAirports: (params) => api.get('/locations/airports', { params }),
  getAllAirports: (params) => api.get('/locations/airports/all', { params }),
  searchAirports: (search) => api.get('/locations/airports/search', { params: { search } }),
  addTravelAirport: (airport_ID) => api.post('/locations/airports', { airport_ID }),
  removeTravelAirport: (id) => api.delete(`/locations/airports/${id}`),
  toggleAirportStatus: (id, enabled) => api.patch(`/locations/airports/${id}/toggle`, { enabled }),
  // Lookups
  getHotelChains: (search, limit) => api.get('/locations/lookups/hotel-chains', { params: { search, limit } }),
  createHotelChain: (data) => api.post('/locations/lookups/hotel-chains', data),
  updateHotelChain: (id, data) => api.put(`/locations/lookups/hotel-chains/${id}`, data),
  deleteHotelChain: (id) => api.delete(`/locations/lookups/hotel-chains/${id}`),
  getHotelTypes: () => api.get('/locations/lookups/hotel-types'),
  createHotelType: (data) => api.post('/locations/lookups/hotel-types', data),
  updateHotelType: (id, data) => api.put(`/locations/lookups/hotel-types/${id}`, data),
  deleteHotelType: (id) => api.delete(`/locations/lookups/hotel-types/${id}`),
  getStates: () => api.get('/locations/lookups/states'),
  getCountries: () => api.get('/locations/lookups/countries'),
};

export const financialAPI = {
  getSummary: (params) => api.get('/financial/summary', { params }),
  getOverview: (params) => api.get('/financial/overview', { params }),
  getByDepartment: (params) => api.get('/financial/by-department', { params }),
  getByTraveler: (params) => api.get('/financial/by-traveler', { params }),
  getByMonth: (params) => api.get('/financial/by-month', { params }),
  getRequests: (params) => api.get('/financial/requests', { params }),
  exportRequests: (params) => api.get('/financial/requests/export', { params }),
  getYears: () => api.get('/financial/years'),
  // Lookups
  getDepartments: () => api.get('/financial/lookups/departments'),
  getSeries: () => api.get('/financial/lookups/series'),
  getStatuses: () => api.get('/financial/lookups/statuses'),
  getTracks: () => api.get('/financial/lookups/tracks'),
  getPromotions: (params) => api.get('/financial/lookups/promotions', { params }),
};

export const eventAPI = {
  getAll: (params) => api.get('/events', { params }),
  getAttendees: (promotionId, params) => api.get(`/events/${promotionId}/attendees`, { params }),
  getCosts: (promotionId) => api.get(`/events/${promotionId}/costs`),
};

export const statsAPI = {
  getSystemStats: () => api.get('/stats'),
  getTrends: (params) => api.get('/stats/trends', { params }),
};

export const draftAPI = {
  getAll: () => api.get('/drafts'),
  getById: (id) => api.get(`/drafts/${id}`),
  create: (data) => api.post('/drafts', data),
  update: (id, data) => api.put(`/drafts/${id}`, data),
  delete: (id) => api.delete(`/drafts/${id}`),
};

export const hotelLookupAPI = {
  // Cache-miss lookups run geocoding (10s) + SerpAPI with retries (30s/attempt)
  // server-side, so they can legitimately outlast the global 30s timeout.
  search: (body) => api.post('/hotel-lookup/search', body, { timeout: 60000 }),
};

export const issueAPI = {
  requestScreenshotUpload: ({ contentType, contentLength }) =>
    api.post('/issues/screenshot-upload', { contentType, contentLength }),
  uploadScreenshotToS3: ({ uploadUrl, file }) =>
    fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type, 'Content-Length': String(file.size) },
      body: file,
    }),
  submit: (payload) => api.post('/issues', payload),
  list: (params = {}) => api.get('/issues', { params }),
  get: (number) => api.get(`/issues/${number}`),
  postComment: (number, body) => api.post(`/issues/${number}/comments`, { body }),
};
